import { Injectable, computed, inject } from '@angular/core';
import { UserStore } from '../user/store/user.store';
import { PermissionService } from '../../core/services/permission.service';

/**
 * Profiles Freely's subscription billing is administered from: the system
 * profile and the billing merchant profile. FTE enforces the same rule
 * (`isBillingAdminProfile`); `FRB` mirrors its default
 * `billing.merchantProfileId`.
 */
export const BILLING_PROFILE_IDS: readonly string[] = ['freely', 'frb'];

export function isBillingProfileId(profileId: string | null | undefined): boolean {
  return !!profileId && BILLING_PROFILE_IDS.includes(profileId.trim().toLowerCase());
}

/**
 * One answer to "may this user see / change billing", shared by the header
 * link, the /billing route guard and the page's write controls so the three
 * cannot disagree.
 *
 * Billing is visible only while the ACTIVE profile is Freely or FRB, and only
 * to a staff user holding BILLING_VIEW there (BILLING_MANAGE to change things).
 * A system admin always qualifies — read from the user-level flag as well as
 * the profile's roles, because an admin switched onto FRB may hold no roles on
 * that profile and PermissionService only looks at the active profile's.
 */
@Injectable({ providedIn: 'root' })
export class BillingAccessService {
  private userStore = inject(UserStore);
  private permissionService = inject(PermissionService);

  readonly isBillingProfile = computed(() =>
    isBillingProfileId(this.userStore.activeProfile()?.profileDetail?.profileId)
  );

  private readonly isSystemAdmin = computed(() => {
    if (this.userStore.userInfo()?.userInfo?.userSettings?.isSystemAdmin) return true;
    return (this.userStore.activeProfile()?.roles ?? []).includes('SYSTEM_ADMIN');
  });

  readonly canView = computed(
    () =>
      this.isBillingProfile() &&
      (this.isSystemAdmin() || this.permissionService.hasPermission(['BILLING_VIEW', 'BILLING_MANAGE']))
  );

  readonly canManage = computed(
    () => this.isBillingProfile() && (this.isSystemAdmin() || this.permissionService.hasPermission('BILLING_MANAGE'))
  );
}
